import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, CreditCard, Loader2 } from "lucide-react";
import QuantitySelector from "@/components/billing/QuantitySelector";
import RefundNotice from "@/components/billing/RefundNotice";
import { startExtraQuoteCheckout, usd } from "@/lib/billingCheckout";
import { formatDate } from "@/lib/dates";

/**
 * What a FixyBee sees when they try to quote with this month's quotes spent
 * (PLANES M4c). Not an error and not a wall: the ways forward and the day the
 * month comes back.
 *
 * Every figure is the server's. `extraPrice`, `extraMax`, `canUpgrade` and
 * `resetsAt` arrive on the 402 the quote endpoint answers (`extra_price_usd`,
 * `extra_max_per_purchase`, `upgrade_available`, `resets_at`), and on the
 * credit snapshot Dany's quote card carries. Without a date the line is simply
 * left out — a guessed date would be worse than none.
 *
 * Extra quotes are bought here, without leaving for the billing page: the
 * same quantity selector as the extra quote card (+1 / +5 / +10 or any other
 * amount up to the cap), the live total, and Stripe Checkout for that
 * quantity. "Upgrade plan" shows only when some plan includes more quotes a
 * month than theirs — never to the plan with the most.
 *
 * Used by the quote form and by Dany's quote card, so the two say the same
 * thing in the same words. `fieldBelowOnSmallPhones` passes through to the
 * selector; only Dany's card sets it (see QuantitySelector).
 */
export default function QuotaChokeCard({
  extraPrice, resetsAt, extraMax = null, canUpgrade = true, testId = "quota-choke",
  fieldBelowOnSmallPhones = false,
}) {
  const { t } = useTranslation();
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // The first of the month as the server drew it, in UTC: on a US clock that
  // instant is still the evening of the 31st.
  const date = formatDate(resetsAt, { timeZone: "UTC" }) || null;

  const buy = async () => {
    if (!qty) return;
    setBusy(true);
    setFailed(false);
    try {
      await startExtraQuoteCheckout(qty);
    } catch {
      setFailed(true);
      setBusy(false);
    }
  };

  return (
    <div className="card-base p-5 border border-sand bg-linen" data-testid={testId}>
      <p className="font-display text-lg font-bold text-worker">{t("quotaChoke.title")}</p>
      {date && (
        <p className="mt-1 text-sm text-smoke" data-testid={`${testId}-resets`}>
          {t("quotaChoke.resets", { date })}
        </p>
      )}
      {extraPrice != null && (
        <div className="mt-4 flex flex-col gap-3" data-testid={`${testId}-extra`}>
          <p className="font-display font-semibold text-worker">
            {t("extraQuote.title")} · <span className="font-mono">{t("extraQuote.price", { price: usd(extraPrice) })}</span>
          </p>
          <QuantitySelector value={qty} onChange={setQty} max={extraMax}
                            testId={`${testId}-qty`}
                            fieldBelowOnSmallPhones={fieldBelowOnSmallPhones} />
          <p className="text-sm text-smoke font-mono" data-testid={`${testId}-total`}>
            {t("billingX.totalLabel")}{" "}
            <span className="font-bold text-worker">{qty ? usd(Number(extraPrice) * qty) : "—"}</span>
          </p>
          <button
            type="button"
            onClick={buy}
            disabled={busy || !qty}
            className="fx-btn fx-btn--primary text-sm"
            data-testid={`${testId}-buy`}
          >
            {busy
              ? <><Loader2 className="animate-spin" size={14} /> {t("billingX.redirecting")}</>
              : <><CreditCard size={14} /> {t("extraQuote.buy", { count: qty || 1 })}</>}
          </button>
        </div>
      )}
      {canUpgrade && (
        <div className="mt-3">
          <Link to="/provider/billing" className="fx-btn fx-btn--secondary text-sm w-full" data-testid={`${testId}-upgrade`}>
            <ArrowUpRight size={14} /> {t("quotaChoke.upgrade")}
          </Link>
        </div>
      )}
      {failed && (
        <p className="mt-2 text-xs text-danger" data-testid={`${testId}-failed`}>
          {t("billingX.couldNotStart")}
        </p>
      )}
      {extraPrice != null && <div className="mt-3"><RefundNotice testId={`${testId}-refund-notice`} /></div>}
    </div>
  );
}
